import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";

/**
 * Lightweight modal confirmation dialog used by the ad form (and reusable
 * elsewhere). Renders a centered card with a title, message, and two
 * actions: Cancel + Confirm.
 *
 * Props:
 *   open          – boolean, show/hide the dialog
 *   title         – heading text
 *   message       – body text shown below the title
 *   confirmLabel  – text for the primary action (default "Confirm")
 *   cancelLabel   – text for the secondary action (default "Cancel")
 *   onConfirm     – called when the user clicks the primary action
 *   onCancel      – called when the user clicks the secondary action
 */
const ConfirmActionDialog = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.45)",
      }}
    >
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          p: 4,
          width: { xs: "90%", sm: 440 },
          boxShadow: 24,
        }}
      >
        <Typography variant="h6" fontWeight={700} mb={1}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {message}
        </Typography>
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <Button
            variant="outlined"
            sx={{
              borderColor: "#1aaefa",
              color: "#1aaefa",
              textTransform: "none",
            }}
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#1aaefa",
              "&:hover": { backgroundColor: "#0d9fe0" },
              textTransform: "none",
            }}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

ConfirmActionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.node.isRequired,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ConfirmActionDialog.defaultProps = {
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
};

export default ConfirmActionDialog;
